// 獨立 PTZ 攝影機的方向鍵節奏（protocol `kinio_ptz.jog`，SPEC #ptz-jog-cadence；Eddy 2026-10-17：按下就一路轉、放開就停）。
// 規則跟 home-base web lib/kinio/ptz.ts（PtzJog / PtzCommandPump，iOS KinioPtzJog 為原始實作）一樣，案例是同一份
// docs/testdata/kinio-ptz-jog.json（從 kinio-app-core / home-base 照抄，jog.test.ts 讀它）：
//   按下 → 立刻送一次短 jog（省略 ms，設備預設 300 ms）；250 ms 內放開 = 單擊，什麼都不再送。
//   按著到 250 ms → 送 `{pan, tilt, ms: 2000}`，之後每 1000 ms 補送；放開送 `kinio_ptz.stop`（設備 2 秒逾時自停當保險）。
//   cancel（分頁隱藏 / 失焦 / 關掉控制盤）→ 按著，或單擊後 300 ms 內設備可能還在轉 → 送 stop。
// 送出順序：跟上一個送出的 jog 同方向就立刻送（起步不等單擊那個回來）；換方向要等路上的 jog 全部回來（只留最新的）；
// stop 立刻送、丟掉還沒送的 jog，路上的 jog 全部回來後再補一次 stop；送過 stop 之後的 jog 一律等。
// jog 最多等 1 秒 ack、504 COMMAND_TIMEOUT 不算錯；stop 等 3 秒。
// 通道（NVR / Station 底下的 ONVIF 攝影機）不走這裡，走 VMS PTZ façade（PtzPanel，lease / 預置點 / 巡航）。

export const JOG_FIRST_HOLD_MS = 250;
export const JOG_RESEND_MS = 1000;
export const JOG_HOLD_MS = 2000;
export const JOG_TAP_MS = 300;
/** jog 等設備 ack 的上限（秒）：等太久只會讓下一個 jog 排隊（gateway 的 0 = 預設 10 秒）；逾時 504 COMMAND_TIMEOUT 不算錯。 */
export const JOG_WAIT_SECONDS = 1;
/** stop 等設備 ack 的上限（秒）。 */
export const STOP_WAIT_SECONDS = 3;

/** 畫面上的方向（pan 右 = 1、tilt 上 = 1）。 */
export interface JogDir {
  pan: -1 | 0 | 1;
  tilt: -1 | 0 | 1;
}

export type JogCommand = { kind: 'jog'; dir: JogDir; ms?: number } | { kind: 'stop' };

const STOP: JogCommand = { kind: 'stop' };

/** 命令 → `POST …/things/{device}/commands` 的 id、payload 與等 ack 的秒數。 */
/** `lens`：多鏡頭機種的焦點鏡頭（設備依那顆的安裝方向換算左右；舊設備忽略，SPEC #multi-lens-ptz）。 */
export function jogRequest(c: JogCommand, lens?: number): { id: string; payload?: Record<string, unknown>; waitSeconds: number } {
  if (c.kind === 'stop') return { id: 'kinio_ptz.stop', waitSeconds: STOP_WAIT_SECONDS };
  const l = lens !== undefined ? { lens } : {};
  return {
    id: 'kinio_ptz.jog',
    payload: c.ms === undefined ? { pan: c.dir.pan, tilt: c.dir.tilt, ...l } : { pan: c.dir.pan, tilt: c.dir.tilt, ms: c.ms, ...l },
    waitSeconds: JOG_WAIT_SECONDS,
  };
}

const same = (a: JogDir | null, b: JogDir) => !!a && a.pan === b.pan && a.tilt === b.tilt;

/** 方向鍵的按住 / 單擊狀態機；每個方法回這一步要送的命令。時間（ms）由呼叫端帶入。 */
export class Jog {
  private dir: JogDir | null = null;
  private holding = false;
  private resendAt: number | null = null;
  private movingUntil: number | null = null;

  get pressed(): boolean {
    return this.dir !== null;
  }

  get nextResendAt(): number | null {
    return this.resendAt;
  }

  press(d: JogDir, now: number): JogCommand[] {
    if (this.dir) return this.change(d, now);
    this.dir = d;
    this.holding = false;
    this.resendAt = now + JOG_FIRST_HOLD_MS;
    this.movingUntil = now + JOG_TAP_MS;
    return [{ kind: 'jog', dir: d }];
  }

  /** 按著換方向；同方向不送。沒按著 = press。 */
  change(d: JogDir, now: number): JogCommand[] {
    if (!this.dir) return this.press(d, now);
    if (same(this.dir, d)) return [];
    this.dir = d;
    return this.hold(now);
  }

  tick(now: number): JogCommand[] {
    if (!this.dir || this.resendAt === null || now < this.resendAt) return [];
    return this.hold(now);
  }

  release(): JogCommand[] {
    if (!this.dir) return [];
    const wasHolding = this.holding;
    this.dir = null;
    this.holding = false;
    this.resendAt = null;
    if (!wasHolding) return []; // 單擊：設備自己轉完 300 ms
    this.movingUntil = null;
    return [STOP];
  }

  cancel(now: number): JogCommand[] {
    const mayMove = this.dir !== null || (this.movingUntil !== null && now < this.movingUntil);
    this.dir = null;
    this.holding = false;
    this.resendAt = null;
    this.movingUntil = null;
    return mayMove ? [STOP] : [];
  }

  private hold(now: number): JogCommand[] {
    const d = this.dir;
    if (!d) return [];
    this.holding = true;
    this.resendAt = now + JOG_RESEND_MS;
    this.movingUntil = now + JOG_HOLD_MS;
    return [{ kind: 'jog', dir: d, ms: JOG_HOLD_MS }];
  }
}

/** 命令送出順序（見檔頭）。`send` 失敗 → `onError`（一次手勢只報第一個，見 resetErrors）。 */
export class JogPump {
  private inFlight = 0;
  private lastDir: JogDir | null = null;
  private pending: JogCommand | null = null;
  private stopAfter = false;
  private reported = false;

  constructor(
    private readonly send: (c: JogCommand) => Promise<unknown>,
    private readonly onError: (e: unknown) => void = () => undefined,
  ) {}

  resetErrors(): void {
    this.reported = false;
  }

  submit(c: JogCommand): void {
    if (c.kind === 'jog') {
      this.stopAfter = false;
      if (this.inFlight > 0 && (this.pending !== null || !same(this.lastDir, c.dir))) this.pending = c;
      else this.startJog(c);
      return;
    }
    this.pending = null;
    this.lastDir = null;
    if (this.inFlight > 0) this.stopAfter = true;
    void this.deliver(c);
  }

  private startJog(c: JogCommand): void {
    this.inFlight++;
    if (c.kind === 'jog') this.lastDir = c.dir;
    void this.deliver(c).then(() => {
      this.inFlight--;
      if (this.inFlight > 0) return;
      if (this.stopAfter) {
        this.stopAfter = false;
        void this.deliver(STOP);
      } else if (this.pending) {
        const next = this.pending;
        this.pending = null;
        this.startJog(next);
      }
    });
  }

  private deliver(c: JogCommand): Promise<void> {
    let p: Promise<unknown>;
    try {
      p = Promise.resolve(this.send(c));
    } catch (e) {
      p = Promise.reject(e);
    }
    return p.then(
      () => undefined,
      (e) => {
        if (!this.reported) {
          this.reported = true;
          this.onError(e);
        }
      },
    );
  }
}
